import React, { useState, useEffect, useMemo, act } from "react";

import TextareaAutosize from "react-autosize-textarea";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  fetchPodcastTranscription,
  updatePodcastProperty,
} from "src/redux/actions/coaching/podcast";

import { useSpring, animated } from "@react-spring/web";

import { Podcast } from "src/types/coaching/podcast";
import Thumbnail from "src/components/thumbnail";
import Reveal from "src/components/animation";
import { TabBar } from "src/components/tabbar";
import { BannerView } from "src/components/banner";

import _ from "lodash";
import cx from "classnames";
import styles from "./details.module.scss";

interface PodcastDetailsProps {
  podcast: Podcast;
}

const PodcastDetails: React.FC<PodcastDetailsProps> = ({ podcast }) => {
  const dispatch = useDispatch();

  const name = useMemo(() => {
    return _.get(podcast, "name", "untitiled podcast");
  }, [podcast]);

  const transcripts = useSelector((state: any) => {
    const transcription = _.get(
      state.coaching.podcasts,
      `edit.item.transcription`,
      null,
    );
    if (transcription) {
      return transcription.data;
    }
    return [];
  });

  const [activeTab, setActiveTab] = useState(0);

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    // handlePropertyChange("description", event.target.value);
  };

  const handleSelectionChange = (action: number) => {
    setActiveTab(action);
  };

  return (
    <section className={styles.container}>
      <span className={styles.header}>
        here&apos;s a customized topics and transcripts for you!
      </span>
      <PodcastDetailCard podcast={podcast} />
      <div className={styles.tabContainer}>
        <TabBar
          className={cx(styles.tabs)}
          gap={32}
          tabSize="auto"
          active={activeTab}
          onSelectionChange={(action) => handleSelectionChange(action)}
        >
          <span className={cx(styles.tab, { [styles.active]: activeTab === 0 })}>
            Topics covered
          </span>
          <span className={cx(styles.tab, { [styles.active]: activeTab === 1 })}>
            Transcript
          </span>
        </TabBar>

        <div className={cx(styles.stacks, styles.tabContentContainer)}>
          <TabSection index={0} selected={activeTab}>
            <div className={styles.topics}>
              {(podcast?.topics || []).map((topic, index) => (
                <div key={index} className={styles.topic}>
                  <div className={styles.name}>{topic.name}</div>
                </div>
              ))}
            </div>
          </TabSection>

          <TabSection index={1} selected={activeTab}>
            <div className={styles.transcripts}>
              {(transcripts || []).map((transcript, index) => {
                return (transcript.conversations || []).map(
                  (conversation, idx) => {
                    return (
                      <div key={idx} className={styles.conversation}>
                        <div className={styles.role}>{conversation.role}</div>
                        <div className={styles.text}>{conversation.text}</div>
                      </div>
                    );
                  },
                );
              })}
            </div>
          </TabSection>
        </div>
      </div>
    </section>
  );
};

export default PodcastDetails;

export const DefaultPodcastDetail = () => {
  const bars = [
    10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 18, 24, 12, 8, 6, 10, 16, 22, 28,
    16, 10, 8, 6, 12, 18, 22, 28, 32, 18, 16, 12, 10, 14, 22, 26, 30, 16, 10,
    10, 10, 10, 10, 10, 10, 10, 10, 10, 18, 24, 12, 8, 6, 10, 16, 22, 28, 16,
    10, 8, 6, 12, 18, 22, 28, 32, 18, 16, 12, 10, 14, 22, 26, 30, 16, 10, 10,
    10, 10, 10, 10, 10, 10, 10, 10, 18, 24, 12, 8, 6, 10, 16, 22, 28, 16, 10, 8,
    6, 12, 18, 22, 28, 32, 18, 16, 12, 10, 14, 22, 26, 30, 16,
  ];
  return (
    <div className={styles.defaultContainer}>
      <div className={styles.audioSection}>
        <img
          src={"/images/coaching/playButton.svg"}
          className={styles.playButton}
        ></img>
        <div className={styles.waveform}>
          {bars.map((height, idx) => (
            <div
              key={idx}
              className={styles.bar}
              style={{ height: `${height}px` }}
            />
          ))}
        </div>
        <span className={styles.duration}>00:00/00:00</span>
      </div>
      <div className={styles.contentSection}>
        <div className={styles.thumbnail}></div>
        <div className={styles.textLines}>
          <div className={styles.lineShort}></div>
          <div className={styles.lineShorter}></div>
        </div>
      </div>
      <div className={styles.description}>
        <div className={styles.descLineLong}></div>
        <div className={styles.descLine}></div>
        <div className={styles.descLine}></div>
        <div className={styles.descShort}></div>
      </div>
    </div>
  );
};

interface PodcastDetailCardProps {
  podcast: Podcast;
}

const PodcastDetailCard: React.FC<PodcastDetailCardProps> = ({ podcast }) => {
  const nameInputRef = React.useRef<any>(null);

  const dispatch = useDispatch();

  const name = useMemo(() => {
    return _.get(podcast, "name") || "";
  }, [podcast]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const thumbnailError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  return (
    <div className={styles.detailsCard}>
      <div className={styles.summaryRow}>
        <div className={cx(styles.field, styles.thumbnail)}>
          <div className={styles.inner}>
            <div className={styles.overlay}>
              <i className="paperflite-edit"></i>
            </div>
            <BannerView
              banner={podcast?.banner as any}
              defaultBanner="/images/assets/default_asset.jpg"
            />
          </div>
          <Reveal show={thumbnailError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{thumbnailError?.message}</span>
            </div>
          </Reveal>
        </div>
        <div className={cx(styles.field, styles.name)}>
          <TextareaAutosize
            className={styles.input}
            ref={nameInputRef}
            placeholder="Enter Podcast name..."
            rows={1}
            maxRows={10}
            value={name}
            data-testid="podcast-name"
            onChange={handleNameChange}
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
          />

          <Reveal show={nameError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{nameError?.message}</span>
            </div>
          </Reveal>
        </div>
      </div>

      <div className={cx(styles.field, styles.configurations)}>
        <div className={styles.item}>
          <i className={cx(styles.duration, "paperflite-clock")} />
          <span>n/a</span>
        </div>
        <div className={styles.item}>
          <span>Type:</span>
          <span>Conversational</span>
        </div>
      </div>
    </div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      opacity: active ? 1 : 0,
      transform: active ? `translate3d(0,0,0)` : `translate3d(0,${100}%, 0)`,
    },
    [selected],
  );

  return active ? (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  ) : (
    <></>
  );
};
